import { useCallback, useEffect, useState } from "react";

import { getStoreBranding as resolveStoreBranding } from "@/config/storeBranding";
import { useAuth } from "@/contexts/AuthContext";
import { CustomerService } from "@/services/features/CustomerService";
import { getCurrentRegion } from "@/utils/environment";

type Avatars = Record<number, string>;
const cache = new Map<string, { expiresAt: number; promise: Promise<Avatars> }>();
const CACHE_TTL_MS = 5 * 60 * 1000;

const loadAvatars = (key: string, customerId?: number): Promise<Avatars> => {
  const cached = cache.get(key);
  if (cached && cached.expiresAt > Date.now()) return cached.promise;

  const service = CustomerService.getInstance();
  const promise = (async () => {
    const response = customerId
      ? await service.getCustomerConfigByCustomerId(customerId)
      : await service.getAllCustomerConfigs();
    if (!response.success) throw new Error("Failed to load customer avatars");
    const configs = Array.isArray(response.data)
      ? response.data
      : response.data
        ? [response.data]
        : [];
    return Object.fromEntries(
      configs.flatMap((config) => {
        const avatar = config.generalConfig?.avatar?.trim();
        return avatar ? [[config.customerId, avatar]] : [];
      }),
    );
  })().catch(() => {
    cache.delete(key);
    return {};
  });
  cache.set(key, { expiresAt: Date.now() + CACHE_TTL_MS, promise });
  return promise;
};

export const useStoreBranding = (customerId?: number) => {
  const { user } = useAuth();
  const isAuthenticated = Boolean(user);
  const region = getCurrentRegion();
  const key = `${region}:${user?.id ?? "anonymous"}:${customerId ?? "all"}`;
  const [state, setState] = useState<{ key: string; avatars: Avatars } | null>(null);

  useEffect(() => {
    if (!isAuthenticated) return;
    let active = true;
    void loadAvatars(key, customerId).then((avatars) => {
      if (active) setState({ key, avatars });
    });
    return () => {
      active = false;
    };
  }, [key, customerId, isAuthenticated]);

  const getStoreBranding = useCallback(
    (id?: number) =>
      resolveStoreBranding(id, state?.key === key && id ? state.avatars[id] : undefined),
    [key, state],
  );
  return { getStoreBranding, isLoading: isAuthenticated && state?.key !== key };
};
